import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { companyAtom } from "@/store/company";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Facility } from "@/types";

// Import CSS separately to avoid SSR issues
import "leaflet/dist/leaflet.css";

// Import the LeafletMap component directly
import LeafletMap from "./map/LeafletMap";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Facility[];
}

const FacilitiesMap: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [company] = useAtom(companyAtom);

  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: ["facilities", company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const urlParams = company?.id ? `?company=${company.id}` : "";
      const response = await fetch(getApiUrl(`facilities/${urlParams}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch facilities");
      }

      return response.json();
    },
  });

  const handleDetails = (facilityId: number) => {
    // navigate(`/facilities/${facilityId}`);
    navigate(`/measure`);
  };

  if (isLoading) {
    return <div>{t("facilities.loading")}</div>;
  }

  if (error) {
    return (
      <div>
        {t("common.error")} {(error as Error).message}
      </div>
    );
  }

  const facilities = data?.results || [];

  return (
    <Card className="overflow-hidden">
      {company?.id ? (
        <div className="w-full p-4" style={{ height: "400px" }}>
          <LeafletMap facilities={facilities} handleDetails={handleDetails} />
        </div>
      ) : null}
    </Card>
  );
};

export default FacilitiesMap;
